// src/pages/payment/PaymentPage.js
import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';

import { doc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';

import { db } from '../../services/firebase/config';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../../services/firebase/config';
import { ArrowLeft } from 'lucide-react';
import PaymentLogos from './PaymentLogos';
import { encryptSensitiveData } from '../../utils/encryption';

//const API_URL = process.env.NODE_ENV === 'development' 
//const API_URL =  'http://127.0.0.1:5001/zq-portfoy/us-central1/api'
const API_URL = 'https://us-central1-zq-portfoy.cloudfunctions.net/api';

const PaymentPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [user] = useAuthState(auth);
  
  // Get plan details from location state with default values
  const planDetails = location.state || { amount: 420, planTitle: 'Aylık ödeme', recurring: true };

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [email] = useState(user?.email || '');
  const [cardHolderName, setCardHolderName] = useState('Test User');
  const [cardNumber, setCardNumber] = useState('9792 0303 9444 0796');
  const [expireMonth, setExpireMonth] = useState('12');
  const [expireYear, setExpireYear] = useState('30');
  const [cvc, setCvc] = useState('000');
  const [address, setAddress] = useState('Test Address');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [orderId] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) return navigate('/login');

    try {
      setLoading(true);
      setError(null);

      const ipResponse = await axios.get('https://api.ipify.org?format=json');
      const userIp = ipResponse.data.ip;

      const orderId = `ORD${Date.now()}`;

      // Firestore'a hassas kart bilgileri olmadan kaydet
      await setDoc(doc(db, '/users/' + user.email + '/orders', orderId), {
        userId: user.email,
        status: 'pending',
        amount: planDetails.amount,
        planType: planDetails.plan, // Plan tipini kaydet
        planTitle: planDetails.planTitle,
        address,
        createdAt: serverTimestamp(),
        orderId,
        cardHolderName,
        paymentStatus: 'initiated'
      });

      // Ödeme verilerini hazırla
      const paymentData = {
        user: user.uid,
        user_ip: userIp,
        amount: planDetails.amount.toString(),
        email: user.email,
        userName: cardHolderName,
        address: address,
        phone: '+905350000000',
        orderId: orderId,
        // Kart bilgilerini şifrele
        cardData: encryptSensitiveData({
          cardNumber: cardNumber.replace(/\s/g, ''),
          cardExpireMonth: expireMonth,
          cardExpireYear: expireYear,
          cardCvc: cvc
        })
      };

      const response = await axios.post(`${API_URL}/payment/paytr`, paymentData);

      if (!response.data || typeof response.data !== 'object') {
        throw new Error('Invalid response from payment server');
      }

      // Update order status
      await updateDoc(doc(db, '/users/' + user.email + '/orders', orderId), {
        paymentStatus: 'processing',
        lastUpdated: serverTimestamp()
      });

      // PayTR form submission
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = 'https://www.paytr.com/odeme';
      form.target = '_self';

      const formData = {
        ...response.data,
        paytr_token: response.data.token
      };
      delete formData.token;

      Object.entries(formData).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          const input = document.createElement('input');
          input.type = 'hidden';
          input.name = key;
          input.value = value.toString();
          form.appendChild(input);
        }
      });

      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);

    } catch (err) {
      console.error('Ödeme hatası:', err);
      const errorMessage = ((err.response && err.response.data && err.response.data.message) || err.message || 'Ödeme işlemi başarısız oldu');
      setError(errorMessage);

      if (orderId) {
        try {
          await updateDoc(doc(db, '/users/' + user.email + '/orders', orderId), {
            paymentStatus: 'failed',
            errorMessage: errorMessage,
            lastUpdated: serverTimestamp()
          });
        } catch (updateErr) {
          console.error('Error updating order status:', updateErr);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  // Kart numarası formatlama
  const formatCardNumber = (value) => {
    const v = value.replace(/\s+/g, '').replace(/[^0-9]/gi, '');
    const matches = v.match(/\d{4,16}/g);
    const match = (matches && matches[0]) || '';
    const parts = [];

    for (let i = 0, len = match.length; i < len; i += 4) {
      parts.push(match.substring(i, i + 4));
    }

    return parts.length ? parts.join(' ') : value;
  };

  return (
    <div className="min-h-screen bg-[#0A0E23] pt-20 lg:pt-12">
      <div className="max-w-6xl mx-auto p-4">
        <div className="bg-[#1A1D1F] rounded-lg">
          <div className="grid lg:grid-cols-2 gap-8 p-6">
            {/* Left Side - Payment Form */}
            <div>
              <button onClick={() => navigate(-1)} className="flex items-center text-gray-400 mb-6">
                <ArrowLeft className="w-4 h-4 mr-2" />
                <span>Geri</span>
              </button>

              <div className="space-y-4">
                <input
                  type="email"
                  value={email}
                  readOnly
                  className="w-full p-3 rounded bg-[#272B30] text-white border-none"
                />
                <p className="text-sm text-gray-400">İletişimler için bu e-posta adresi kullanılacak</p>

                <h2 className="text-white text-lg mt-6">Ödeme bilgileri</h2>

                <input
                  type="text"
                  value={cardHolderName}
                  onChange={e => setCardHolderName(e.target.value)}
                  placeholder="Kart üzerindeki isim"
                  className="w-full p-3 rounded bg-[#272B30] text-white border-none"
                />

                <input
                  type="text"
                  value={cardNumber}
                  onChange={e => setCardNumber(formatCardNumber(e.target.value))}
                  placeholder="Kart Numarası"
                  maxLength="19"
                  className="w-full p-3 rounded bg-[#272B30] text-white border-none"
                />

                <div className="grid grid-cols-3 gap-4">
                  <input
                    type="text"
                    value={expireMonth}
                    onChange={e => setExpireMonth(e.target.value)}
                    placeholder="Ay"
                    maxLength="2"
                    className="p-3 rounded bg-[#272B30] text-white border-none"
                  />
                  <input
                    type="text"
                    value={expireYear}
                    onChange={e => setExpireYear(e.target.value)}
                    placeholder="Yıl"
                    maxLength="2"
                    className="p-3 rounded bg-[#272B30] text-white border-none"
                  />
                  <input
                    type="text"
                    value={cvc}
                    onChange={e => setCvc(e.target.value)}
                    placeholder="CVC"
                    maxLength="3"
                    className="p-3 rounded bg-[#272B30] text-white border-none"
                  />
                </div>

                <h2 className="text-white text-lg mt-6">Fatura bilgileri</h2>
                <textarea
                  value={address}
                  onChange={e => setAddress(e.target.value)}
                  placeholder="Fatura Adresi"
                  rows={3}
                  className="w-full p-3 rounded bg-[#272B30] text-white border-none resize-none"
                />

                <div className="flex items-center mt-6">
                  <input
                    type="checkbox"
                    checked={acceptTerms}
                    onChange={e => setAcceptTerms(e.target.checked)}
                    className="mr-2"
                  />
                  <label className="text-sm text-gray-300">
                    Ön Bilgilendirme Formu ve Mesafeli Satış Sözleşmesini okudum ve onaylıyorum.
                  </label>
                </div>

                {error && (
                  <div className="bg-red-500/10 text-red-500 p-3 rounded">
                    {error}
                  </div>
                )}
              </div>
            </div>

            {/* Right Side - Order Summary */}
            <div className="p-6">
              <div className="mb-8">
                <h1 className="text-gray-400 mb-4">Abonelik</h1>
                <div className="flex justify-between items-center mb-2">
                  <span className="text-white text-xl">Smart Portföy Pro ({planDetails.planTitle})</span>
                  <span className="text-white text-xl">{planDetails.amount} ₺</span>
                </div>

                <button className="text-blue-500 text-sm hover:underline">
                  Kupon kodu kullan
                </button>

                <div className="mt-4 pt-4 border-t border-gray-700">
                  <div className="flex justify-between items-center">
                    <span className="text-white font-bold">Toplam</span>
                    <div className="text-right">
                      <div className="text-white font-bold">{planDetails.amount} ₺</div>
                      <div className="text-sm text-gray-400">KDV dahildir</div>
                    </div>
                  </div>
                </div>

                <button
                  onClick={handleSubmit}
                  disabled={loading || !acceptTerms}
                  className="w-full bg-blue-600 text-white p-3 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 mt-6"
                >
                  {loading ? 'İşleniyor...' : 'Ödemeyi tamamla'}
                </button>

                <PaymentLogos />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentPage;